import { useCallback } from 'react';
import { CaseStudyItem, CaseStudySection, CaseStudySectionType, CASE_STUDY_SECTIONS, ContentBlockType, CONTENT_TYPES } from '@/types/case-study';
import { useCaseStudy } from '@/hooks/useCaseStudy';

interface UseSectionOperationsProps {
  caseStudy: CaseStudyItem;
  setCaseStudy: React.Dispatch<React.SetStateAction<CaseStudyItem>>;
  setActiveSection: (sectionId: string | null) => void;
  scrollToSection: (sectionId: string) => void;
  isNewCaseStudy: boolean;
  onSectionContentChanged?: (sectionId: string, oldContent: string, newContent: string) => void;
}

interface UseSectionOperationsReturn {
  handleAddSection: (sectionType: CaseStudySectionType, insertAfterSectionId?: string) => Promise<void>;
  handleAddCustomSection: (title: string, description?: string) => Promise<void>;
  handleAddSectionFromElement: (elementType: ContentBlockType, insertAfterSectionId?: string) => Promise<void>;
  handleUpdateSection: (sectionId: string, updates: Partial<CaseStudySection>) => Promise<void>;
  handleRemoveSection: (sectionId: string) => Promise<void>;
}

export const useSectionOperations = ({
  caseStudy,
  setCaseStudy,
  setActiveSection,
  scrollToSection,
  isNewCaseStudy,
  onSectionContentChanged,
}: UseSectionOperationsProps): UseSectionOperationsReturn => {
  const { addSection, updateSection, deleteSection } = useCaseStudy();

  const handleAddSection = useCallback(async (sectionType: CaseStudySectionType, insertAfterSectionId?: string) => {
    // Determine insertion position
    let insertIndex = caseStudy.sections.length;
    if (insertAfterSectionId) {
      const targetIndex = caseStudy.sections.findIndex(s => s.id === insertAfterSectionId);
      if (targetIndex !== -1) {
        insertIndex = targetIndex + 1;
      }
    }

    const newSection: CaseStudySection = {
      id: crypto.randomUUID(),
      type: sectionType,
      order: insertIndex,
      title: CASE_STUDY_SECTIONS.find(s => s.type === sectionType)?.title || "",
      content: "",
      images: [],
      isCollapsed: false,
    };

    // Update local state immediately for UI responsiveness
    setCaseStudy(prev => {
      const newSections = [...prev.sections];
      newSections.splice(insertIndex, 0, newSection);

      // Update order numbers for all sections
      const reorderedSections = newSections.map((section, index) => ({
        ...section,
        order: index,
      }));

      return {
        ...prev,
        sections: reorderedSections,
      };
    });
    setActiveSection(newSection.id);

    // Scroll to the new section after a brief delay to ensure DOM update
    setTimeout(() => {
      scrollToSection(newSection.id);
    }, 100);

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        await addSection.mutateAsync({
          caseStudyId: caseStudy.id,
          section: newSection,
        });
      } catch (error) {
        console.error('Error adding section:', error);
        // Revert local state on error
        setCaseStudy(prev => ({
          ...prev,
          sections: prev.sections.filter(s => s.id !== newSection.id),
        }));
      }
    }
  }, [caseStudy.sections, caseStudy.id, isNewCaseStudy, addSection, setCaseStudy, setActiveSection, scrollToSection]);

  const handleAddCustomSection = useCallback(async (title: string, description?: string) => {
    const newSection: CaseStudySection = {
      id: crypto.randomUUID(),
      type: "custom",
      order: caseStudy.sections.length,
      title: title,
      content: description || "",
      images: [],
      isCollapsed: false,
    };

    // Update local state immediately for UI responsiveness
    setCaseStudy(prev => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
    setActiveSection(newSection.id);

    // Scroll to the new section after a brief delay to ensure DOM update
    setTimeout(() => {
      scrollToSection(newSection.id);
    }, 100);

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        await addSection.mutateAsync({
          caseStudyId: caseStudy.id,
          section: newSection,
        });
      } catch (error) {
        console.error('Error adding custom section:', error);
        // Revert local state on error
        setCaseStudy(prev => ({
          ...prev,
          sections: prev.sections.filter(s => s.id !== newSection.id),
        }));
      }
    }
  }, [caseStudy.sections.length, caseStudy.id, isNewCaseStudy, addSection, setCaseStudy, setActiveSection, scrollToSection]);

  // Helper function to get default content for content block type
  const getDefaultContentForType = useCallback((type: ContentBlockType): Record<string, unknown> => {
    switch (type) {
      case "text":
        return { text: "" };
      case "image":
        return { url: "", caption: "" };
      case "video":
        return { url: "", caption: "", type: "url" };
      case "gallery":
        return { images: [] };
      case "carousel":
        return { images: [] };
      case "line-break":
        return {};
      case "quote":
        return { text: "", author: "" };
      case "callout":
        return { variant: "info", text: "" };
      default:
        return {};
    }
  }, []);

  const handleAddSectionFromElement = useCallback(async (elementType: ContentBlockType, insertAfterSectionId?: string) => {
    // Determine insertion position
    let insertIndex = caseStudy.sections.length;
    if (insertAfterSectionId) {
      const targetIndex = caseStudy.sections.findIndex(s => s.id === insertAfterSectionId);
      if (targetIndex !== -1) {
        insertIndex = targetIndex + 1;
      }
    }

    // Get element type display name
    const elementInfo = CONTENT_TYPES.find(ct => ct.type === elementType);
    const sectionTitle = elementInfo?.title || elementType;

    // Create the content block
    const newBlock = {
      id: crypto.randomUUID(),
      type: elementType,
      content: getDefaultContentForType(elementType),
      order: 0,
    };

    // Create the section with the content block
    const newSection: CaseStudySection = {
      id: crypto.randomUUID(),
      type: "custom",
      order: insertIndex,
      title: sectionTitle,
      content: "",
      images: [],
      isCollapsed: false,
      hideTitle: true, // Hide title in preview for element-only sections
      content_blocks: [newBlock],
    };

    // Update local state immediately for UI responsiveness
    setCaseStudy(prev => {
      const newSections = [...prev.sections];
      newSections.splice(insertIndex, 0, newSection);

      // Update order numbers for all sections
      const reorderedSections = newSections.map((section, index) => ({
        ...section,
        order: index,
      }));

      return {
        ...prev,
        sections: reorderedSections,
      };
    });
    setActiveSection(newSection.id);

    // Scroll to the new section after a brief delay to ensure DOM update
    setTimeout(() => {
      scrollToSection(newSection.id);
    }, 100);

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        await addSection.mutateAsync({
          caseStudyId: caseStudy.id,
          section: newSection,
        });
      } catch (error) {
        console.error('Error adding section from element:', error);
        // Revert local state on error
        setCaseStudy(prev => ({
          ...prev,
          sections: prev.sections.filter(s => s.id !== newSection.id),
        }));
      }
    }
  }, [caseStudy.sections, caseStudy.id, isNewCaseStudy, addSection, setCaseStudy, setActiveSection, scrollToSection, getDefaultContentForType]);

  const handleUpdateSection = useCallback(async (sectionId: string, updates: Partial<CaseStudySection>) => {
    // Get the current section to track content changes
    const currentSection = caseStudy.sections.find(section => section.id === sectionId);
    const oldContent = currentSection?.content || '';
    const newContent = updates.content || oldContent;
    
    // Check if content has changed and notify callback
    if (oldContent !== newContent && onSectionContentChanged) {
      onSectionContentChanged(sectionId, oldContent, newContent);
    }
    
    // Update local state immediately
    setCaseStudy(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      ),
    }));

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        await updateSection.mutateAsync({
          caseStudyId: caseStudy.id,
          sectionId,
          updates,
        });
      } catch (error) {
        console.error('Error updating section:', error);
      }
    }
  }, [caseStudy.id, caseStudy.sections, isNewCaseStudy, updateSection, setCaseStudy, onSectionContentChanged]);

  const handleRemoveSection = useCallback(async (sectionId: string) => {
    // Find the section to be deleted and its index
    const deletedIndex = caseStudy.sections.findIndex(s => s.id === sectionId);
    
    // Update local state immediately
    setCaseStudy(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.id !== sectionId),
    }));
    setActiveSection(null);

    // Find the nearest section to scroll to after deletion
    const remainingSections = caseStudy.sections.filter(s => s.id !== sectionId);
    let sectionToScrollTo: string | null = null;
    
    if (remainingSections.length > 0) {
      if (deletedIndex > 0) {
        // If we deleted a section that wasn't the first, scroll to the previous section
        sectionToScrollTo = remainingSections[deletedIndex - 1].id;
      } else {
        // If we deleted the first section, scroll to the new first section
        sectionToScrollTo = remainingSections[0].id;
      }
    }

    // Scroll after a brief delay
    setTimeout(() => {
      if (sectionToScrollTo) {
        // Scroll to the nearest section
        scrollToSection(sectionToScrollTo);
        setActiveSection(sectionToScrollTo);
      } else {
        // If no sections remain, scroll to the top of the form
        const scrollContainer = document.querySelector('[data-scroll-container]') as HTMLDivElement | null;
        if (
          scrollContainer &&
          scrollContainer.scrollHeight > scrollContainer.clientHeight + 5
        ) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    }, 100);

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        await deleteSection.mutateAsync({ caseStudyId: caseStudy.id, sectionId });
      } catch (error) {
        console.error('Error removing section:', error);
      }
    }
  }, [caseStudy.sections, caseStudy.id, isNewCaseStudy, deleteSection, setCaseStudy, setActiveSection, scrollToSection]);

  return {
    handleAddSection,
    handleAddCustomSection,
    handleAddSectionFromElement,
    handleUpdateSection,
    handleRemoveSection,
  };
};
